import { useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { Star, TrendingUp, TrendingDown, X, Users, CheckCircle2, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';

const EROGATORI = [
  { id: 1, nome: 'Coop. Salute Roma', sla: '32h', rifiuto: '4%', esecuzione: '96%', prems: 4.3, score: 92, trend: 'up', contratto: 'Scad. 12/2026', budget: '€ 1.8M', pazienti: 186 },
  { id: 2, nome: 'Consorzio ADI Lazio', sla: '28h', rifiuto: '2%', esecuzione: '98%', prems: 4.5, score: 96, trend: 'up', contratto: 'Scad. 06/2027', budget: '€ 2.2M', pazienti: 234 },
  { id: 3, nome: 'Fondazione Gemelli@Home', sla: '45h', rifiuto: '8%', esecuzione: '91%', prems: 3.8, score: 78, trend: 'down', contratto: 'Scad. 03/2026', budget: '€ 1.5M', pazienti: 142 },
  { id: 4, nome: 'ASL Roma 2 — Diretto', sla: '52h', rifiuto: '12%', esecuzione: '87%', prems: 3.5, score: 68, trend: 'down', contratto: 'Interno', budget: '€ 800K', pazienti: 98 },
  { id: 5, nome: 'Coop. Riabilita Roma', sla: '36h', rifiuto: '5%', esecuzione: '94%', prems: 4.1, score: 88, trend: 'up', contratto: 'Scad. 09/2026', budget: '€ 1.2M', pazienti: 156 },
];

const RANKING_DATA = EROGATORI
  .sort((a, b) => b.score - a.score)
  .map(e => ({ nome: e.nome.length > 22 ? e.nome.slice(0, 22) + '…' : e.nome, fullNome: e.nome, score: e.score, id: e.id }));

function scoreColor(score) {
  if (score >= 90) return '#10b981';
  if (score >= 75) return '#f59e0b';
  return '#ef4444';
}

function ScoreBadge({ score }) {
  const color = score >= 90 ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : score >= 75 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
  return <Badge className={`${color} border-0 text-[10px] font-bold`}>{score}/100</Badge>;
}

function RankingTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white dark:bg-slate-900 p-3 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 text-xs">
      <p className="font-bold text-slate-800 dark:text-white mb-1">{d.fullNome}</p>
      <p className="text-slate-500">Score Complessivo: <strong>{d.score}/100</strong></p>
      <p className="text-slate-400 text-[10px] mt-1">{d.score >= 90 ? 'Performance eccellente' : d.score >= 75 ? 'In linea con target' : 'Sotto le aspettative'}</p>
    </div>
  );
}

export default function ASLPerformance() {
  const [selectedErog, setSelectedErog] = useState(null);

  return (
    <AppLayout role="asl">
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="asl-performance-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Performance Erogatori</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Vendor Rating — Comparativa e drill-down contrattuale</p>
        </div>

        {/* Ranking Horizontal Bar Chart */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="ranking-chart">
          <h2 className="text-sm font-bold text-slate-800 dark:text-white mb-1">Ranking Erogatori — Score Complessivo</h2>
          <p className="text-[10px] text-slate-400 mb-4">Classifica basata su SLA, tasso rifiuto, esecuzione PAI e PREMs</p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={RANKING_DATA} layout="vertical" margin={{ top: 0, right: 20, left: 15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e2e8f0)" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis dataKey="nome" type="category" tick={{ fontSize: 10, fill: '#94a3b8' }} width={160} />
                <Tooltip content={<RankingTooltip />} />
                <Bar dataKey="score" name="Score" radius={[0, 6, 6, 0]} barSize={20}>
                  {RANKING_DATA.map((entry, index) => (
                    <Cell key={`rank-${index}`} fill={scoreColor(entry.score)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-[10px] text-slate-400">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> 90+ Eccellente</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> 75-89 In linea</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> &lt;75 Sotto target</span>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="ranking-table">
          <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800">
            <h2 className="text-sm font-bold text-slate-800 dark:text-white">Dettaglio Indicatori per Erogatore</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700/60">
                  {['#', 'Erogatore', 'SLA (Presa in Carico)', 'Tasso Rifiuto', '% Prestazioni vs PAI', 'PREMs', 'Score', 'Trend', ''].map((col, idx) => (
                    <th key={idx} className="px-5 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {EROGATORI.sort((a, b) => b.score - a.score).map((e, i) => (
                  <tr key={e.id} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors" data-testid={`erogatore-row-${e.id}`}>
                    <td className="px-5 py-4 text-sm font-bold text-slate-400">{i + 1}</td>
                    <td className="px-5 py-4">
                      <p className="text-sm font-semibold text-slate-800 dark:text-white">{e.nome}</p>
                      <p className="text-[10px] text-slate-400">{e.contratto}</p>
                    </td>
                    <td className="px-5 py-4 text-xs font-medium text-slate-700 dark:text-slate-200">{e.sla}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-medium ${parseFloat(e.rifiuto) > 7 ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-200'}`}>{e.rifiuto}</span>
                    </td>
                    <td className="px-5 py-4 text-xs font-medium text-slate-700 dark:text-slate-200">{e.esecuzione}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        <span className="text-xs font-bold text-slate-800 dark:text-white">{e.prems}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4"><ScoreBadge score={e.score} /></td>
                    <td className="px-5 py-4">
                      {e.trend === 'up' ? <TrendingUp className="w-4 h-4 text-emerald-500" /> : <TrendingDown className="w-4 h-4 text-red-500" />}
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => setSelectedErog(e)} className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors" data-testid={`drilldown-${e.id}`}>Dettaglio</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Drawer Dettaglio Erogatore */}
      {selectedErog && (
        <div className="fixed inset-0 z-50" data-testid="drawer-erogatore">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedErog(null)} />
          <div className="absolute top-0 right-0 h-full w-full max-w-[500px] bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
              <h2 className="text-sm font-bold text-slate-800 dark:text-white">{selectedErog.nome}</h2>
              <button onClick={() => setSelectedErog(null)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="drawer-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5">
              {/* Score visual */}
              <div className="flex items-center gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-white text-lg font-bold ${selectedErog.score >= 90 ? 'bg-emerald-500' : selectedErog.score >= 75 ? 'bg-amber-500' : 'bg-red-500'}`}>
                  {selectedErog.score}
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800 dark:text-white">Score Complessivo: {selectedErog.score}/100</p>
                  <p className="text-xs text-slate-400">{selectedErog.score >= 90 ? 'Performance eccellente' : selectedErog.score >= 75 ? 'In linea con gli standard' : 'Performance sotto la media'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { l: 'PREMs', v: `${selectedErog.prems}/5.0` },
                  { l: 'SLA Presa in Carico', v: selectedErog.sla },
                  { l: 'Tasso Rifiuto', v: selectedErog.rifiuto },
                  { l: '% Prestazioni vs PAI', v: selectedErog.esecuzione },
                  { l: 'Pazienti in Carico', v: selectedErog.pazienti },
                  { l: 'Budget Annuo', v: selectedErog.budget },
                ].map(item => (
                  <div key={item.l} className="px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">{item.l}</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-white mt-0.5">{item.v}</p>
                  </div>
                ))}
              </div>
              <div className={`rounded-xl border p-4 ${selectedErog.score < 80 ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800/40' : 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/40'}`}>
                <p className={`text-xs font-semibold mb-1 ${selectedErog.score < 80 ? 'text-red-800 dark:text-red-300' : 'text-emerald-800 dark:text-emerald-300'}`}>Note per Rinegoziazione</p>
                <p className={`text-xs leading-relaxed ${selectedErog.score < 80 ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                  {selectedErog.score < 80 ? 'Performance sotto la media. Si consiglia convocazione per piano di miglioramento con penali contrattuali.' : 'Performance in linea con gli standard. Valutare rinnovo con premialità per mantenimento SLA.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
